import React, { useState } from 'react';
import QuestionIcon from '@/icons/QuestionIcon';
import { useModal } from '@/app/context/ModalContext/useModal';
import { useSettings } from '@/app/context/SettingsContext/useSettings';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';
import { supportedLanguages } from '@/app/constants/SupportedLanguages';

interface IProps {
  title: string;
  options: string[];
  helpText?: string;
  optionChildren?: (React.ComponentType | null)[];
  selectedOption: number;
  onSelect: (index: number) => void;
  popularOption?: string;
  showGeneratorBehavior?: boolean;
  showLanguage?: boolean;
}

const RadioFormElement = (props: IProps) => {
  const { openModal } = useModal();
  const { generatorBehavior, flashcardLanguage } = useSettings();
  const { trackEvent, analyticsInstance } = useAnalytics();

  const languageDisplayName =
    supportedLanguages.find((l) => l.value === flashcardLanguage).name ||
    'Auto-Detect';

  return (
    <>
      <div className="bo mb-4 mt-6 flex gap-2">
        {props.title}{' '}
        {props.helpText && (
          <span
            className="tooltip inline-block cursor-pointer"
            data-tip={props.helpText}
          >
            <QuestionIcon className="scale-90 fill-base-content" />
          </span>
        )}
        <div className="flex-1"></div>
        {props.showLanguage && (
          <div className="hidden text-base-content xl:block">
            <span className="hidden xl:inline-block">Language:</span>
            <span>
              {' '}
              <button
                onClick={() => {
                  trackEvent(analyticsInstance, 'generate_form_click_language');
                  openModal('settings');
                }}
                className="link"
              >
                {languageDisplayName}
              </button>
            </span>
          </div>
        )}
        {props.showGeneratorBehavior && (
          <div className="hidden text-base-content md:block">
            <span className="hidden md:inline-block">Density:</span>
            <span>
              {' '}
              <button
                onClick={() => {
                  trackEvent(analyticsInstance, 'generate_form_click_density');
                  openModal('settings');
                }}
                className="link"
              >
                {generatorBehavior === 'Default' ? 'Medium' : 'High'}
              </button>
            </span>
          </div>
        )}
      </div>
      {props.options.map((option, index) => {
        const ChildComponent = props.optionChildren
          ? props.optionChildren[index]
          : () => null;

        return (
          <React.Fragment key={index}>
            <div className="form-control">
              <label
                className={`label cursor-pointer rounded pl-2 ${
                  index === props.selectedOption
                    ? 'bg-neutral text-neutral-content'
                    : 'text-base-content'
                }`}
              >
                <span className="text-sm">
                  {option}
                  {false &&
                    props.popularOption &&
                    props.popularOption === option && (
                      <div className="badge ml-2 bg-primary">most popular</div>
                    )}
                </span>
                <input
                  type="radio"
                  name={props.title}
                  className="checked:bg-red-500 radio-secondary radio-sm"
                  onChange={() => props.onSelect(index)}
                  checked={props.selectedOption === index}
                />
              </label>
            </div>
            <div>{props.selectedOption === index && <ChildComponent />}</div>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default RadioFormElement;
